import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Heart, Users, Compass, Beaker, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { BRAND } from "@/constants/brand";
import { ManagedImage } from "@/hooks/useAssets";

const VISION = [
  { icon: Beaker, title: "Innovation", text: "A lab that never stops. Recipes tested in public, learnings shared openly." },
  { icon: Sparkles, title: "Quality", text: "Single-origin ingredients, slow-set freezing, honest labels." },
  { icon: MapPin, title: "Expansion", text: "Karnataka. India. The world. Slow, considered, real growth." },
  { icon: Heart, title: "Customer Experiences", text: "Inviting customers into the brand, not just selling to them." },
  { icon: Users, title: "Student Opportunities", text: "Open to anyone who wants to contribute, collaborate or help grow the brand." },
  { icon: Compass, title: "Community Engagement", text: "Inventors, voters and neighbours shaping the future of Pop Panda." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero / Our Story */}
      <section className="section-pad pb-10">
        <div className="container-pp grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">About Pop Panda</div>
            <motion.h1 initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.7}}
              className="editorial-h1 text-5xl md:text-6xl mt-4">A brand born from curiosity.</motion.h1>
            <p className="mt-8 text-brand-charcoal/70 text-lg leading-relaxed">
              Pop Panda started with a simple vision — make frozen treats people actually love. Not the kind that look good for a photo, but the kind that surprise you, make you slow down, and stay with you.
            </p>
            <div className="mt-8 space-y-5 text-brand-charcoal/75 leading-relaxed">
              <p>We didn't begin with a factory. We began with curiosity — and a freezer.</p>
              <p>Every batch since has been an experiment. We test recipes. We share lab notes. We learn from customers who walk into Pop Panda Go with brutally honest opinions. We learn from suppliers who challenge our assumptions. And we keep going.</p>
              <p>What you see today is the result of countless small experiments — and the start of many more.</p>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            <ManagedImage slot="about.gallery1" alt="Ingredients" fallback="https://images.unsplash.com/photo-1556679343-c7306c1976bc?q=80&w=1400&auto=format&fit=crop" className="rounded-3xl object-cover aspect-[3/4] col-span-1 row-span-2" />
            <ManagedImage slot="about.gallery2" alt="Lab" fallback="https://images.unsplash.com/photo-1605781231474-f60dea478e8a?q=80&w=1400&auto=format&fit=crop" className="rounded-3xl object-cover aspect-[4/3]" />
            <ManagedImage slot="about.gallery3" alt="Popsicles" fallback="https://images.unsplash.com/photo-1488900128323-21503983a07e?q=80&w=1400&auto=format&fit=crop" className="rounded-3xl object-cover aspect-[4/3]" />
          </div>
        </div>
      </section>

      {/* Built by Students */}
      <section className="section-pad" data-testid="about-students">
        <div className="container-pp grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="card-elevated p-10 lg:p-14 grain-overlay bg-brand-charcoal text-brand-cream relative overflow-hidden">
              <div className="absolute -right-10 -bottom-10 text-[12rem] opacity-10 font-display leading-none">★</div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-yellow">Our Origin</div>
              <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">Built by Students.<br /><em className="italic">Driven by Dreams.</em></h2>
              <p className="mt-8 text-brand-cream/80 leading-relaxed">
                Pop Panda is a reflection of what can happen when curiosity, hard work, and creativity come together. What began during a student journey continues to grow through learning, innovation, and the support of customers and communities.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">A student-driven beginning</div>
            <h3 className="font-display text-4xl mt-3 leading-tight">A brand that grew up between exams and experiments.</h3>
            <ul className="mt-8 space-y-4 text-brand-charcoal/80">
              {[
                "Developed during a student journey — between classes, holidays and trial batches.",
                "Built while balancing education and entrepreneurship — the hard way, the slow way.",
                "Shaped through experimentation, customer feedback and honest learning.",
                "Grown through real-world business experience — invoices, suppliers, breakdowns and breakthroughs.",
                "Driven by creativity, ambition and an unfair amount of determination.",
              ].map((p, i) => (
                <li key={i} className="flex gap-4">
                  <span className="font-display text-2xl text-brand-red leading-none">0{i+1}</span>
                  <span className="leading-relaxed pt-0.5">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Just Getting Started */}
      <section className="section-pad bg-white" data-testid="about-getting-started">
        <div className="container-pp">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">The road ahead</div>
              <h2 className="font-display text-5xl md:text-7xl mt-3 leading-none">We Are Just Getting Started.</h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-brand-charcoal/70 text-lg leading-relaxed">Every new customer, every new idea, and every new challenge helps shape the future of Pop Panda.</p>
            </div>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { t: "Continuous Improvement", d: "Each batch teaches us something. Each season improves the next." },
              { t: "Product Innovation", d: "Our Innovation Lab is always running. Always failing forward." },
              { t: "Manufacturing Growth", d: "Slow, deliberate scaling — quality before quantity." },
              { t: "Customer-Inspired Ideas", d: "Inventors submit ideas. We make some real. The community votes." },
              { t: "Community Participation", d: "Vote, request, suggest, taste. You are part of the kitchen." },
              { t: "Expansion Opportunities", d: "Dealers, franchisees, schools, hotels — we're listening for the right partners." },
            ].map((b) => (
              <div key={b.t} className="card-elevated p-7">
                <div className="font-display text-2xl">{b.t}</div>
                <p className="mt-3 text-sm text-brand-charcoal/70 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Innovation & Community */}
      <section className="section-pad" data-testid="about-innovation">
        <div className="container-pp grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <ManagedImage slot="about.innovation" alt="Innovation" fallback="https://images.unsplash.com/photo-1605781231474-f60dea478e8a?q=80&w=1400&auto=format&fit=crop" className="rounded-3xl w-full aspect-[4/3] object-cover" />
          </div>
          <div className="lg:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Innovation & Community</div>
            <h2 className="font-display text-5xl mt-3 leading-none">Customers help shape the menu.</h2>
            <p className="mt-6 text-brand-charcoal/70 leading-relaxed">Our Innovation Lab is where it all begins — a working studio of prototypes, failed batches and breakthroughs. But the most exciting part isn't the lab. It's the community.</p>
            <ul className="mt-6 space-y-3 text-brand-charcoal/80 text-sm">
              <li>· Customers invent popsicles in the Flavour Creator Studio.</li>
              <li>· Submissions are voted by the community — Love It, Must Try, Make Now.</li>
              <li>· Top-voted ideas enter our Innovation Journey: Idea → Review → Testing → Prototype → Trial → Production → Stores.</li>
              <li>· Every contributor sees their creation move through the stages on the Hall of Fame.</li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/flavour-creator" className="btn-pill btn-dark text-sm" data-testid="about-create">Create Your Own Popsicle <ArrowUpRight size={14} className="arrow" /></Link>
              <Link to="/hall-of-fame" className="btn-pill btn-outline-dark text-sm" data-testid="about-hof">Visit Hall of Fame</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="section-pad bg-brand-charcoal text-brand-cream" data-testid="about-vision">
        <div className="container-pp">
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-yellow">Vision for the Future</div>
          <h2 className="font-display text-5xl md:text-7xl mt-3 leading-none text-brand-cream max-w-4xl">A premium Indian popsicle brand, shaped by curiosity and community.</h2>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VISION.map((v) => (
              <div key={v.title} className="rounded-3xl border border-white/10 bg-white/5 p-7">
                <v.icon size={22} className="text-brand-yellow" />
                <div className="font-display text-2xl mt-4">{v.title}</div>
                <p className="mt-2 text-sm text-brand-cream/75 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad" data-testid="about-cta">
        <div className="container-pp card-elevated p-10 lg:p-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Join the journey</div>
            <h2 className="font-display text-4xl md:text-5xl mt-3">{BRAND.tagline}.</h2>
            <p className="mt-4 text-brand-charcoal/70 max-w-xl">Whether you're a customer, a student, a future inventor or a future franchisee — there's a way in.</p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <Link to="/student-opportunities" className="btn-pill btn-dark text-sm" data-testid="about-student-cta">Student Opportunities</Link>
            <Link to="/franchise" className="btn-pill btn-outline-dark text-sm" data-testid="about-franchise-cta">Franchise</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
